import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { getProfile } from './user';

const initialState = {
  isLoading: false,
  error: null,
  decks: [],
  folders: [],
  activeFolderId: 'all', // 'all' | 'unfiled' | <folderId>
  searchQuery: '',
};

const slice = createSlice({
  name: 'decks',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setDecks(state, action) {
      state.isLoading = false;
      state.decks = action.payload;
    },
    setFolders(state, action) {
      state.folders = action.payload;
    },
    upsertDeck(state, action) {
      const deck = action.payload;
      const i = state.decks.findIndex((d) => d.id === deck.id);
      if (i === -1) state.decks.unshift(deck);
      else state.decks[i] = deck;
    },
    removeDeck(state, action) {
      state.decks = state.decks.filter((d) => d.id !== action.payload);
    },
    upsertFolder(state, action) {
      const folder = action.payload;
      const i = state.folders.findIndex((f) => f.id === folder.id);
      if (i === -1) state.folders.push(folder);
      else state.folders[i] = folder;
      state.folders.sort((a, b) => a.name.localeCompare(b.name));
    },
    removeFolder(state, action) {
      state.folders = state.folders.filter((f) => f.id !== action.payload);
      state.decks = state.decks.map((d) => (d.folderId === action.payload ? { ...d, folderId: null } : d));
      if (state.activeFolderId === action.payload) state.activeFolderId = 'all';
    },
    setActiveFolder(state, action) {
      state.activeFolderId = action.payload;
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
  },
});

export default slice.reducer;
export const { setActiveFolder, setSearchQuery } = slice.actions;

const API = process.env.REACT_APP_HOST_API;

// ----------------------------------------------------------------------

export function fetchDecks({ folderId, search } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const params = {};
      if (folderId && folderId !== 'all') params.folderId = folderId;
      if (search) params.search = search;
      const response = await axios.get(`${API}/decks`, { params });
      dispatch(slice.actions.setDecks(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Decks/Fetch/InternalError'));
    }
  };
}

export function fetchFolders() {
  return async (dispatch) => {
    try {
      const response = await axios.get(`${API}/folders`);
      dispatch(slice.actions.setFolders(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.error?.message || 'Folders/Fetch/InternalError'));
    }
  };
}

export function createDeck({ title, folderId, color }) {
  return async (dispatch) => {
    const response = await axios.post(`${API}/decks`, { title, folderId, color });
    dispatch(slice.actions.upsertDeck(response.data.data));
    return response.data.data;
  };
}

export function importDeck(formData) {
  return async (dispatch) => {
    // No explicit Content-Type here — axios/the browser must set multipart/form-data
    // with its own boundary from the FormData instance; hardcoding the header strips
    // that boundary and breaks multer's parsing on the server.
    const response = await axios.post(`${API}/decks/import`, formData);
    dispatch(slice.actions.upsertDeck(response.data.data));
    // Refreshes state.user.profile.importsRemaining so the rest of the app reflects today's
    // import allowance being spent, without a second bespoke reducer for it.
    dispatch(getProfile());
    return response.data.data;
  };
}

export function updateDeck(id, { title, folderId, color }) {
  return async (dispatch) => {
    const response = await axios.patch(`${API}/decks/${id}`, { title, folderId, color });
    dispatch(slice.actions.upsertDeck(response.data.data));
    return response.data.data;
  };
}

export function deleteDeck(id) {
  return async (dispatch) => {
    await axios.delete(`${API}/decks/${id}`);
    dispatch(slice.actions.removeDeck(id));
  };
}

export function createFolder({ name }) {
  return async (dispatch) => {
    const response = await axios.post(`${API}/folders`, { name });
    dispatch(slice.actions.upsertFolder(response.data.data));
    return response.data.data;
  };
}

export function renameFolder(id, name) {
  return async (dispatch) => {
    const response = await axios.patch(`${API}/folders/${id}`, { name });
    dispatch(slice.actions.upsertFolder(response.data.data));
    return response.data.data;
  };
}

export function deleteFolder(id) {
  return async (dispatch) => {
    await axios.delete(`${API}/folders/${id}`);
    dispatch(slice.actions.removeFolder(id));
  };
}
